/* A1CW · Diagnostic Quote Builder
   Geocodes the user's address (Nominatim), routes from home base (OSRM),
   falls back to straight-line × 1.25 if routing is unavailable.
   Pricing: base_fee + per_mile_rate × distance × 2 (round trip).
   Privacy: the address goes to Nominatim/OSRM only when the visitor clicks
   Calculate or presses Enter in the address field, never while typing.
*/
(function () {
  'use strict';

  const script = document.currentScript;
  if (!script) return;

  const HOME_LAT     = parseFloat(script.dataset.homeLat)  || 35.117125;
  const HOME_LON     = parseFloat(script.dataset.homeLon)  || -81.087649;
  const BASE_FEE     = parseFloat(script.dataset.baseFee)  || 200;
  const PER_MILE     = parseFloat(script.dataset.perMile)  || 0.70;
  const MAX_RADIUS   = parseFloat(script.dataset.maxRadius) || 100; // straight-line miles from the service-area center
  const AREA_LAT     = parseFloat(script.dataset.areaLat)   || 35.2271;  // Charlotte, NC
  const AREA_LON     = parseFloat(script.dataset.areaLon)   || -80.8431;

  const $ = (id) => document.getElementById(id);

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function money(n) {
    return '$' + (Math.round(n * 100) / 100).toFixed(2);
  }

  // Haversine distance in miles
  function haversineMiles(lat1, lon1, lat2, lon2) {
    const R = 3958.7613; // Earth radius miles
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  async function geocode(address) {
    const url =
      'https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=us&q=' +
      encodeURIComponent(address);
    const res = await fetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) throw new Error('Geocode failed (' + res.status + ')');
    const data = await res.json();
    if (!data || !data.length) throw new Error('Address not found');
    return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon), display: data[0].display_name };
  }

  async function routeMiles(fromLat, fromLon, toLat, toLon) {
    // OSRM public demo — driving profile, distance in meters
    const url =
      'https://router.project-osrm.org/route/v1/driving/' +
      fromLon + ',' + fromLat + ';' + toLon + ',' + toLat +
      '?overview=false&alternatives=false&steps=false';
    const res = await fetch(url);
    if (!res.ok) throw new Error('Routing failed');
    const data = await res.json();
    if (!data.routes || !data.routes.length) throw new Error('No route');
    return data.routes[0].distance / 1609.344; // meters → miles
  }

  function setStatus(msg, tone) {
    const el = $('QbStatus');
    if (!el) return;
    el.textContent = msg;
    el.className = 'qb-quote-panel__status' + (tone ? ' qb-quote-panel__status--' + tone : '');
  }

  function setQuote(distMiles) {
    const round = distMiles * 2;
    const mileageCost = round * PER_MILE;
    const total = BASE_FEE + mileageCost;

    $('QbAmount').innerHTML =
      '$' + Math.floor(total) +
      '<span class="qb-quote-panel__amount-sub">.' + (total.toFixed(2).split('.')[1]) + '</span>';

    $('QbBreakdown').hidden = false;
    $('QbBase').textContent = money(BASE_FEE);
    $('QbRoundTrip').textContent = round.toFixed(1) + ' mi (' + distMiles.toFixed(1) + ' mi each way)';
    // Per-mile rate is intentionally not exposed to the customer — hide that breakdown row.
    var mileageEl = $('QbMileage');
    if (mileageEl && mileageEl.closest('li')) mileageEl.closest('li').style.display = 'none';
    $('QbTotal').textContent = money(total);

    $('QbDistance').value = distMiles.toFixed(2);
    $('QbQuote').value    = total.toFixed(2);
    // 2026-10-18: phones see the fee right under the address (the panel sits below the whole form there).
    var inl = $('QbInline');
    if (inl) { inl.textContent = 'Estimated diagnostic fee: ' + money(total) + ' (includes travel; confirmed before dispatch)'; inl.hidden = false; }
  }

  function resetQuote(message) {
    var inl = $('QbInline'); if (inl) inl.hidden = true;
    $('QbAmount').innerHTML = '$' + BASE_FEE + '<span class="qb-quote-panel__amount-sub">.00</span>';
    $('QbBreakdown').hidden = true;
    $('QbDistance').value = '';
    $('QbQuote').value    = '';
    $('QbGeoLat').value   = '';
    $('QbGeoLon').value   = '';
    setStatus(message || 'Enter your address, then click Calculate.');
  }

  // Invariant: the hidden estimate fields are filled only while lastQuery is set.
  let lastQuery = '';   // address the current estimate was calculated for ('' = no estimate)
  let pendingAddr = ''; // address of the calculation in flight
  let inflight = 0;     // request token; bumping it cancels whatever is in flight
  let busy = false;
  let btnLabel = '';

  function focusLost() {
    return !document.activeElement || document.activeElement === document.body;
  }

  function setBusy(on) {
    busy = on;
    const btn = $('QbCalculate');
    if (!btn) return;
    btn.disabled = on;
    btn.textContent = on ? 'Calculating…' : btnLabel;
  }

  // Drop the estimate and cancel any request in flight, with no network call.
  function invalidate(message) {
    lastQuery = '';
    ++inflight;
    setBusy(false);
    resetQuote(message);
  }

  async function calculate() {
    const input = $('QbAddress');
    const btn = $('QbCalculate');
    if (!input || busy) return;
    const addr = input.value.trim();
    if (addr.length < 8) {
      invalidate('Enter your full address, then click Calculate.');
      return;
    }
    if (addr === lastQuery) return; // unchanged since the last successful calculation

    const myReq = ++inflight;
    const refocus = btn && document.activeElement === btn; // disabling the button drops its focus
    // Different address: the previous estimate no longer applies, on screen or in the form.
    lastQuery = '';
    pendingAddr = addr;
    resetQuote();
    setBusy(true);
    setStatus('Calculating travel…', 'loading');

    try {
      const geo = await geocode(addr);
      if (myReq !== inflight) return; // stale

      let miles;
      try {
        miles = await routeMiles(HOME_LAT, HOME_LON, geo.lat, geo.lon);
      } catch (_) {
        // fallback: straight-line × 1.25 to approximate road distance
        miles = haversineMiles(HOME_LAT, HOME_LON, geo.lat, geo.lon) * 1.25;
      }
      if (myReq !== inflight) return;
      if (input.value.trim() !== addr) { // changed without an input event (autofill etc.)
        invalidate('Address changed. Click Calculate to update.');
        if (refocus && focusLost()) btn.focus(); // finally skips this: invalidate() advanced the token
        return;
      }

      const fromCenter = haversineMiles(AREA_LAT, AREA_LON, geo.lat, geo.lon);
      if (fromCenter > MAX_RADIUS) {
        setStatus(
          "That address is about " + Math.ceil(fromCenter) + " miles from Charlotte. That is beyond our " + MAX_RADIUS + "-mile service area. You can still submit your request, and we'll let you know whether we can make the trip.",
          'warn'
        );
      } else {
        setStatus('Calculated for ' + (geo.display ? geo.display.split(',').slice(0, 2).join(',') : addr), 'ok');
      }
      setQuote(miles);
      // Coordinates are written only with the final result, so a mid-calculation submit can't send them without a fee.
      $('QbGeoLat').value = geo.lat.toFixed(6);
      $('QbGeoLon').value = geo.lon.toFixed(6);
      lastQuery = addr;
    } catch (err) {
      if (myReq !== inflight) return;
      resetQuote("Couldn't find that address. Double-check spelling, city & ZIP."); // lastQuery stays '' so a retry works
    } finally {
      if (myReq === inflight) {
        setBusy(false);
        if (refocus && focusLost()) btn.focus();
      }
    }
  }

  const PREFIX_RE = /^— Estimated diagnostic visit fee: \$[\d.]+ \(one-way distance ~[\d.]+ mi\)\n\n/;

  ready(function () {
    const addrInput = $('QbAddress');
    if (!addrInput) return; // block not on this page

    const btn = $('QbCalculate');
    if (btn) {
      btnLabel = btn.textContent.trim();
      btn.addEventListener('click', calculate);
    }
    addrInput.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || e.isComposing || e.keyCode === 229) return;
      e.preventDefault(); // Enter here calculates; it must not submit the form
      calculate();
    });
    // Editing the address after (or during) a calculation invalidates the estimate, with no network call.
    function onAddressEdit() {
      const value = addrInput.value.trim();
      if (busy ? value !== pendingAddr : (lastQuery && value !== lastQuery)) {
        invalidate('Address changed. Click Calculate to update.');
      }
    }
    addrInput.addEventListener('input', onAddressEdit);
    addrInput.addEventListener('change', onAddressEdit);

    // Prepend the quote breakdown into the email body on submit
    const form = $('QbForm');
    if (form) {
      form.addEventListener('submit', function () {
        const issue = $('QbIssue');
        if (!issue) return;
        // An estimate only counts for the address it was calculated for.
        if (lastQuery && addrInput.value.trim() !== lastQuery) {
          invalidate('Address changed. Click Calculate to update.'); // clears the panel and the hidden fields
        }
        issue.value = issue.value.replace(PREFIX_RE, ''); // no duplicate prefix on a resubmit
        const distance = $('QbDistance').value;
        const quote    = $('QbQuote').value;
        if (distance && quote) {
          issue.value =
            '— Estimated diagnostic visit fee: $' + quote +
            ' (one-way distance ~' + distance + ' mi)\n\n' +
            issue.value;
        }
      });
    }
  });
})();
